import { UserService } from '../../api/services/UserService';
import { BusinessBrandingService } from '../../api/services/BusinessBrandingService';
import { CompanySettingsService } from '../../api/services/CompanySettingsService';
import { CompanyWorkScheduleService } from '../../api/services/CompanyWorkScheduleService';
import { GoogleCalendarService } from '../../api/services/GoogleCalendarService';
import { ProfileService } from '../../api/services/ProfileService';
import type { Routemize_DTOs_OnboardingProgressDto } from '../../api/models/Routemize_DTOs_OnboardingProgressDto';
import type { Routemize_DTOs_UpdateOnboardingProgressDto } from '../../api/models/Routemize_DTOs_UpdateOnboardingProgressDto';
import type { Routemize_DTOs_CompanySettingsDTO } from '../../api/models/Routemize_DTOs_CompanySettingsDTO';
import { Routemize_Constants_AddressType } from '../../api/models/Routemize_Constants_AddressType';
import { AddressManager } from '../users/AddressManager';

export interface StepValidation {
  stepId: number;
  isValid: boolean;
  reason?: string;
}

export interface ResumeResult {
  resumeStep: number;
  completedSteps: number[];
  isCompleted: boolean;
}

/**
 * Onboarding is eight single-purpose screens.
 *
 * Each screen asks for one thing so the control the user came for is always
 * the first thing on the page. The previous five-screen flow stacked several
 * decisions per screen, which pushed the important ones (home address, travel
 * radius, which Google calendar to sync) below the fold on smaller viewports —
 * people either scrolled past them or never found them at all.
 *
 * The flow deliberately ends on Google Calendar rather than on a booking link:
 * a brand-new form still needs its questions and appointment times set before
 * it is worth sending to anyone, so finishing hands the user straight to
 * Booking Forms instead of to a link they aren't ready to share.
 */
export const TOTAL_ONBOARDING_STEPS = 8;

export const ONBOARDING_STEP = {
  BRAND: 0,
  PROFILE: 1,
  HOME: 2,
  BASE: 3,
  RADIUS: 4,
  HOURS: 5,
  TRAVEL: 6,
  CALENDAR: 7,
} as const;

/**
 * Screens the user can legitimately leave without writing anything.
 *
 * Everything else is judged purely on whether its record exists, so this is
 * the only place the stored progress list is trusted.
 */
const ADVANCEABLE_WITHOUT_DATA: number[] = [ONBOARDING_STEP.CALENDAR];

/**
 * OnboardingManager - progress persistence, data-backed step validation, and
 * resume detection.
 *
 * Validation is deliberately driven by the real records rather than by the
 * stored step counter: a user who configured something in Settings and then
 * re-entered the wizard should not be asked for it twice, and a stored counter
 * that runs ahead of the data should not skip a step that was never filled in.
 */
export class OnboardingManager {
  private addressManager: AddressManager;
  // Progress is read by every onboarding-status check plus the wizard resume.
  // Cache it on this singleton (with in-flight dedupe) so it is one shared
  // fetch; invalidated whenever progress is written.
  private progressCache: Routemize_DTOs_OnboardingProgressDto | null = null;
  private progressInFlight: Promise<Routemize_DTOs_OnboardingProgressDto> | null = null;

  constructor() {
    this.addressManager = new AddressManager(UserService);
  }

  async getProgress(forceRefresh = false): Promise<Routemize_DTOs_OnboardingProgressDto> {
    if (!forceRefresh && this.progressCache) return this.progressCache;
    if (!forceRefresh && this.progressInFlight) return this.progressInFlight;
    this.progressInFlight = UserService.getApiAppUserGetOnboardingProgress()
      .then(p => {
        this.progressCache = p;
        return p;
      })
      .finally(() => {
        this.progressInFlight = null;
      });
    return this.progressInFlight;
  }

  async updateProgress(currentStep: number, completedSteps: number[]): Promise<void> {
    const input: Routemize_DTOs_UpdateOnboardingProgressDto = {
      currentStep,
      completedSteps,
    };
    await UserService.putApiAppUserUpdateOnboardingProgress(input);
    this.progressCache = null;
  }

  /**
   * Validate every step against the record it writes.
   *
   * Company settings back four of the checks, so they are fetched once and
   * shared rather than re-requested per step.
   */
  async validateAllSteps(userId: string): Promise<StepValidation[]> {
    const companySettings = await CompanySettingsService.getApiAppCompanySettingsGet().catch(
      () => null
    );

    const [brand, profile, home, hours, calendar] = await Promise.all([
      this.validateBrandStep(),
      this.validateProfileStep(),
      this.validateHomeStep(userId),
      this.validateHoursStep(),
      this.validateCalendarStep(),
    ]);

    return [
      { stepId: ONBOARDING_STEP.BRAND, ...brand },
      { stepId: ONBOARDING_STEP.PROFILE, ...profile },
      { stepId: ONBOARDING_STEP.HOME, ...home },
      { stepId: ONBOARDING_STEP.BASE, ...this.validateBaseStep(companySettings) },
      { stepId: ONBOARDING_STEP.RADIUS, ...this.validateRadiusStep(companySettings) },
      { stepId: ONBOARDING_STEP.HOURS, ...hours },
      { stepId: ONBOARDING_STEP.TRAVEL, ...this.validateTravelStep(companySettings) },
      { stepId: ONBOARDING_STEP.CALENDAR, ...calendar },
    ];
  }

  /**
   * Decide where to drop the user back in.
   *
   * Progress recorded against an earlier version of the wizard is discarded —
   * those indices mean something different now, so replaying them would land
   * people on the wrong screen. Data validation reconstructs the true state.
   */
  async determineResumeStep(userId: string): Promise<ResumeResult> {
    const progress = await this.getProgress();

    if (progress.isCompleted) {
      return {
        resumeStep: TOTAL_ONBOARDING_STEPS - 1,
        completedSteps: Array.from({ length: TOTAL_ONBOARDING_STEPS }, (_, i) => i),
        isCompleted: true,
      };
    }

    const validations = await this.validateAllSteps(userId);
    const isValid = (stepId: number) =>
      validations.find(v => v.stepId === stepId)?.isValid ?? false;

    const storedCompleted = progress.completedSteps ?? [];
    const isLegacyProgress = storedCompleted.some(id => id >= TOTAL_ONBOARDING_STEPS);
    const trustedCompleted = isLegacyProgress ? [] : storedCompleted;

    // A step counts as done when its record exists. The two screens that can be
    // advanced past without writing anything also honour the stored list, so
    // someone who deliberately skipped Google Calendar isn't sent back to it.
    const completedSteps: number[] = [];
    for (let i = 0; i < TOTAL_ONBOARDING_STEPS; i++) {
      const done = ADVANCEABLE_WITHOUT_DATA.includes(i)
        ? isValid(i) || trustedCompleted.includes(i)
        : isValid(i);
      if (done) completedSteps.push(i);
    }

    // Resume on the first gap so nobody is dropped past an empty screen.
    const firstIncomplete = Array.from({ length: TOTAL_ONBOARDING_STEPS }, (_, i) => i).find(
      i => !completedSteps.includes(i)
    );

    return {
      resumeStep: firstIncomplete ?? TOTAL_ONBOARDING_STEPS - 1,
      completedSteps,
      isCompleted: false,
    };
  }

  // Step 0 — Brand: branding is identified by the business name.
  private async validateBrandStep(): Promise<{ isValid: boolean; reason?: string }> {
    try {
      const branding = await BusinessBrandingService.getApiAppBusinessBrandingGet();
      return branding.businessName?.trim()
        ? { isValid: true }
        : { isValid: false, reason: 'Business name not set' };
    } catch {
      return { isValid: false, reason: 'Could not load branding data' };
    }
  }

  // Step 1 — Profile: the contact details customers see on a booking.
  private async validateProfileStep(): Promise<{ isValid: boolean; reason?: string }> {
    try {
      const profile = await ProfileService.getApiAccountMyProfile();
      if (!profile.name?.trim() || !profile.surname?.trim()) {
        return { isValid: false, reason: 'Name not complete' };
      }
      if (!profile.phoneNumber?.trim()) {
        return { isValid: false, reason: 'Phone number not set' };
      }
      return { isValid: true };
    } catch {
      return { isValid: false, reason: 'Could not load profile' };
    }
  }

  // Step 2 — Home: the address routes are measured from.
  private async validateHomeStep(userId: string): Promise<{ isValid: boolean; reason?: string }> {
    try {
      const addresses = await this.addressManager.getUserAddresses(userId);
      const hasHome = (addresses ?? []).some(
        a => a.addressType === Routemize_Constants_AddressType.HOME
      );
      return hasHome ? { isValid: true } : { isValid: false, reason: 'Home address not set' };
    } catch {
      return { isValid: false, reason: 'Could not load addresses' };
    }
  }

  // Step 3 — Base: where the working day starts and ends.
  private validateBaseStep(settings: Routemize_DTOs_CompanySettingsDTO | null): {
    isValid: boolean;
    reason?: string;
  } {
    if (!settings) return { isValid: false, reason: 'Could not load company settings' };

    const prefs = settings.routePreferences;
    if (!prefs?.defaultStartingLocation) {
      return { isValid: false, reason: 'Start location not set' };
    }
    if (!prefs.defaultEndingLocation) {
      return { isValid: false, reason: 'End location not set' };
    }
    return { isValid: true };
  }

  // Step 4 — Radius: how far the user is willing to travel.
  private validateRadiusStep(settings: Routemize_DTOs_CompanySettingsDTO | null): {
    isValid: boolean;
    reason?: string;
  } {
    if (!settings) return { isValid: false, reason: 'Could not load company settings' };
    return settings.maxTravelRadiusMiles && settings.maxTravelRadiusMiles > 0
      ? { isValid: true }
      : { isValid: false, reason: 'Service radius not set' };
  }

  // Step 5 — Hours: the company work week. This reads the company work
  // schedule because that is what the step writes; per-user work hours are a
  // separate record and are never set here.
  private async validateHoursStep(): Promise<{ isValid: boolean; reason?: string }> {
    try {
      const schedule =
        await CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule();
      const hasWorkDay = (schedule?.dailySchedules ?? []).some(d => d.isWorkDay);
      return hasWorkDay
        ? { isValid: true }
        : { isValid: false, reason: 'No work days configured' };
    } catch {
      return { isValid: false, reason: 'Could not load work schedule' };
    }
  }

  // Step 6 — Travel: how driving time is counted around appointments.
  private validateTravelStep(settings: Routemize_DTOs_CompanySettingsDTO | null): {
    isValid: boolean;
    reason?: string;
  } {
    const prefs = settings?.routePreferences;
    return prefs?.firstAppointmentScheduling && prefs.lastAppointmentScheduling
      ? { isValid: true }
      : { isValid: false, reason: 'Travel time rules not set' };
  }

  // Step 7 — Calendar: a bound calendar, not merely an authorized account.
  private async validateCalendarStep(): Promise<{ isValid: boolean; reason?: string }> {
    const status = await this.getCalendarConnection();
    return status.syncedCalendarCount > 0
      ? { isValid: true }
      : { isValid: false, reason: 'No Google calendar selected' };
  }

  /**
   * Split the two Google states the rest of the app kept conflating.
   *
   * `isAuthorized` only means a Google token exists — signing into Routemize
   * with Google produces one before the user has agreed to anything about
   * calendars. Nothing syncs until a specific calendar is bound, so
   * `syncedCalendarCount` is the signal worth showing as "connected".
   */
  async getCalendarConnection(): Promise<{
    isAuthorized: boolean;
    syncedCalendarCount: number;
  }> {
    try {
      const info = await GoogleCalendarService.getApiAppGoogleCalendarGetCalendarIntegrationInfo();
      return {
        isAuthorized: Boolean(info?.isConnectedToGoogle),
        syncedCalendarCount: info?.connectedCalendars?.length ?? 0,
      };
    } catch {
      return { isAuthorized: false, syncedCalendarCount: 0 };
    }
  }
}

export const onboardingManager = new OnboardingManager();
